import { useState } from "react";
import { useEnergy } from "../../hooks/useEnergy";
import { useWeather } from "../../hooks/useWeather";
import { useSolar } from "../../hooks/useSolar";
import { usePeakLoad } from "../../hooks/usePeakLoad";
import { estimateDieselEmissions } from "../../utils/emissions";
import { GaugeCard } from "../../components/molecules/GaugeCard/GaugeCard";
import { Card } from "../../components/atoms/Card/Card";
import { LoadingSkeleton } from "../../components/atoms/LoadingSkeleton/LoadingSkeleton";
import { BottomSheet } from "../../components/molecules/BottomSheet/BottomSheet";
import { CAPACITY } from "../../constants";
import { motion } from "framer-motion";
import { AnimatedNumber } from "../../components/atoms/AnimatedNumber/AnimatedNumber";
import "./CurrentEnergy.css";

function toTitle(gaugeType: string) {
  const clean = gaugeType
    .replace("Total On-Island ", "")
    .replace("Renewable Power ", "")
    .replace("plus Battery Generation", "")
    .replace("Generation", "");
  if (clean === "Exported Off Island") {
    return "FLOWING OFF ISLAND";
  }
  if (clean === "Used On Island") {
    return "ON ISLAND LOAD";
  }
  return clean;
}

type SheetType = "totalLoad" | "nbImport" | "wind" | "solar" | "fossil" | null;

export function CurrentEnergy() {
  const { data: energyData, isLoading, error } = useEnergy();
  const { data: weatherData } = useWeather();
  const { data: solarData } = useSolar();
  const { data: peakLoadData } = usePeakLoad();
  const [openSheet, setOpenSheet] = useState<SheetType>(null);

  // Use dynamic peak from database, fallback to hardcoded constant
  const peakLoad = peakLoadData?.value ?? CAPACITY.peakLoad;

  const peakTimeInfo = (() => {
    if (!peakLoadData?.startTime) {
      return { line1: "", line2: "Feb 2023" };
    }

    const start = new Date(peakLoadData.startTime);
    const end = new Date(peakLoadData.endTime);

    // Get relative date string
    const getRelativeDate = (date: Date): string => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);

      const diffDays = Math.floor((today.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));
      const diffWeeks = Math.floor(diffDays / 7);
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffDays / 365);

      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffWeeks === 1) return "Last week";
      if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
      if (diffMonths === 1) return "Last month";
      if (diffMonths < 12) return `${diffMonths} months ago`;
      if (diffYears === 1) return "Last year";
      return `${diffYears} years ago`;
    };

    const formatTime = (date: Date): string => {
      return date.toLocaleTimeString("en-US", {
        timeZone: "America/Halifax",
        hour: "numeric",
        minute: "2-digit",
      });
    };

    const relativeDate = getRelativeDate(start);

    // Line 1: relative date with time range
    let timeRange = "";
    if (start.toDateString() === end.toDateString()) {
      if (start.getTime() === end.getTime()) {
        timeRange = formatTime(start);
      } else {
        timeRange = `${formatTime(start)}-${formatTime(end)}`;
      }
    }

    // Line 2: absolute date (weekday, month day year)
    const absoluteDate = start.toLocaleDateString("en-US", {
      timeZone: "America/Halifax",
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return {
      line1: `${relativeDate} ${timeRange}`.trim(),
      line2: absoluteDate,
    };
  })();

  if (isLoading) {
    return (
      <div className="CurrentEnergy-skeletonGrid">
        <LoadingSkeleton variant="chart" />
        <LoadingSkeleton variant="chart" />
        <LoadingSkeleton variant="chart" />
      </div>
    );
  }

  if (error) {
    return <p className="CurrentEnergy-error">Failed to load energy data</p>;
  }

  if (!energyData || energyData.length === 0) {
    return <p className="CurrentEnergy-noData">No energy data available</p>;
  }

  // Calculate import from NB: Load - Wind - Solar - Fossil
  const calculateImportFromNB = () => {
    const load =
      energyData.find((g) => g.gaugeType === "Total On-Island Load")?.value ||
      0;
    const wind =
      energyData.find((g) => g.gaugeType === "Total On-Island Wind Generation")
        ?.value || 0;
    const solar =
      energyData.find(
        (g) => g.gaugeType === "Total On-Island Solar plus Battery Generation",
      )?.value || 0;
    const fossil =
      energyData.find(
        (g) => g.gaugeType === "Total On-Island Fossil Fuel Generation",
      )?.value || 0;

    const totalGeneration = wind + solar + fossil;
    const importValue = Math.max(0, load - totalGeneration);

    return {
      value: Math.round(importValue * 100) / 100,
      percentage: Math.round((importValue / CAPACITY.nbImportMax) * 100),
    };
  };

  // Get current generation values
  const getCurrentGeneration = () => {
    const wind =
      energyData.find((g) => g.gaugeType === "Total On-Island Wind Generation")
        ?.value || 0;
    const solar =
      energyData.find(
        (g) => g.gaugeType === "Total On-Island Solar plus Battery Generation",
      )?.value || 0;
    const fossil =
      energyData.find(
        (g) => g.gaugeType === "Total On-Island Fossil Fuel Generation",
      )?.value || 0;

    return { wind, solar, fossil };
  };

  // Reliable capacity: firm NB imports + on-island generators (excludes wind/solar)
  const reliableCapacity = CAPACITY.reliableCapacity;

  // Calculate possible capacity: all sources at max
  const possibleCapacity =
    CAPACITY.nbImportMax +
    CAPACITY.fossilFuelMax +
    CAPACITY.solarMax +
    CAPACITY.windMax;

  const nbImport = calculateImportFromNB();
  const generation = getCurrentGeneration();
  const loadGauge = energyData.find(
    (g) => g.gaugeType === "Total On-Island Load",
  );

  const formatTimestamp = (ts: string) => {
    return new Date(ts).toLocaleString("en-US", {
      timeZone: "America/Halifax",
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  // Get gradient color based on percentage (green -> yellow -> red)
  const getCapacityGradient = (percent: number) => {
    if (percent >= 95) {
      return "linear-gradient(90deg, #f85149, #da3633)"; // Red
    } else if (percent >= 85) {
      // Yellow to orange
      const t = (percent - 85) / 10; // 0 to 1
      return `linear-gradient(90deg, #d29922, ${t > 0.5 ? "#f85149" : "#e3b341"})`;
    } else {
      return "linear-gradient(90deg, #3fb950, #238636)"; // Green
    }
  };

  // Get text color based on percentage
  const getCapacityColor = (percent: number) => {
    if (percent >= 95) {
      return "#f85149"; // Red
    } else if (percent >= 85) {
      return "#d29922"; // Yellow/Orange
    } else {
      return "#3fb950"; // Green
    }
  };

  return (
    <div className="CurrentEnergy">
      {/* Demand Section */}
      <div className="CurrentEnergy-section">
        <h3 className="CurrentEnergy-subsectionTitle">Demand</h3>
        <div className="CurrentEnergy-grid">
          {/* Card 1: Total Load + Import from NB stats */}
          {loadGauge && (
            <Card className="CurrentEnergy-statsCard">
              <div className="CurrentEnergy-statsRow">
                {/* Total Load */}
                <div className="CurrentEnergy-statBlock">
                  <div className="CurrentEnergy-totalLoadHeader">
                    <h3>Total Load</h3>
                  </div>
                  <div className="CurrentEnergy-totalLoadDualValueContainer">
                    <div className="CurrentEnergy-totalLoadValue">
                      <AnimatedNumber
                        value={loadGauge.value}
                        decimals={0}
                        className="CurrentEnergy-totalLoadValueText"
                      />
                      <span className="CurrentEnergy-totalLoadUnit">MW</span>
                    </div>
                    <div
                      className="CurrentEnergy-totalLoadValue"
                      style={{
                        color: loadGauge.value >= peakLoad
                          ? '#f85149' // red when at or surpassing peak
                          : loadGauge.value >= peakLoad * 0.95
                            ? '#d29922' // yellow when approaching (95%+)
                            : undefined // blue (default)
                      }}
                    >
                      <AnimatedNumber
                        value={(loadGauge.value / peakLoad) * 100}
                        decimals={1}
                        className="CurrentEnergy-totalLoadValueText"
                      />
                      <span className="CurrentEnergy-totalLoadUnit">
                        % of peak
                      </span>
                    </div>
                  </div>
                  <div className="CurrentEnergy-totalLoadBar">
                    <motion.div
                      className="CurrentEnergy-totalLoadFill"
                      initial={{
                        width: `${Math.min(100, (loadGauge.value / peakLoad) * 100)}%`,
                      }}
                      animate={{
                        width: `${Math.min(100, (loadGauge.value / peakLoad) * 100)}%`,
                      }}
                      transition={{ duration: 1, ease: "easeInOut" }}
                    />
                  </div>
                  <div className="CurrentEnergy-totalLoadRange" style={{ textAlign: 'right' }}>
                    {loadGauge.value >= peakLoad - 1 ? (
                      <>
                        <div>{Math.round(loadGauge.value)} MW Right Now</div>
                        <div style={{ opacity: 0.7 }}>{peakTimeInfo.line2}</div>
                      </>
                    ) : (
                      <>
                        <div>Previous Peak: {Math.round(peakLoad)} MW {peakTimeInfo.line1}</div>
                        <div style={{ opacity: 0.7 }}>{peakTimeInfo.line2}</div>
                      </>
                    )}
                  </div>
                </div>

                {/* Import from NB */}
                {nbImport && nbImport.value > 0 && (
                  <div className="CurrentEnergy-statBlock">
                    <div className="CurrentEnergy-nbImportHeader">
                      <h3>Import from NB</h3>
                    </div>
                    <div className="CurrentEnergy-nbImportDualValueContainer">
                      <div className="CurrentEnergy-nbImportValue">
                        <AnimatedNumber
                          value={nbImport.value}
                          decimals={0}
                          className="CurrentEnergy-nbImportValueText"
                        />
                        <span className="CurrentEnergy-nbImportUnit">MW</span>
                      </div>
                      <div className="CurrentEnergy-nbImportValue">
                        <AnimatedNumber
                          value={nbImport.percentage}
                          decimals={0}
                          className="CurrentEnergy-nbImportValueText"
                        />
                        <span className="CurrentEnergy-nbImportUnit">
                          % of max
                        </span>
                      </div>
                    </div>
                    <div className="CurrentEnergy-nbImportBar">
                      <motion.div
                        className="CurrentEnergy-nbImportFill"
                        initial={{ width: `${nbImport.percentage}%` }}
                        animate={{ width: `${nbImport.percentage}%` }}
                        transition={{ duration: 1, ease: "easeInOut" }}
                      />
                    </div>
                    <div className="CurrentEnergy-nbImportRange">
                      0 - {CAPACITY.nbImportMax} MW capacity
                    </div>
                  </div>
                )}
              </div>
              <button
                className="CurrentEnergy-infoButton"
                onClick={() => setOpenSheet("nbImport")}
                aria-label="More info about demand"
              >
                i
              </button>
            </Card>
          )}

          {/* Card 2: Breakdown and Capacity Gauges */}
          {loadGauge && (
            <Card className="CurrentEnergy-capacityCard">
              <div className="CurrentEnergy-capacityCardHeader">
                <h3>Current Breakdown</h3>
              </div>
              {/* 1. Current Breakdown - Segmented bar showing where power comes from */}
              <div className="CurrentEnergy-capacitySection">
                <div className="CurrentEnergy-segmentedBar">
                  <motion.div
                    className="CurrentEnergy-segment CurrentEnergy-segmentNB"
                    style={{
                      width: `${(nbImport.value / loadGauge.value) * 100}%`,
                    }}
                    title={`NB Import: ${Math.round(nbImport.value)} MW`}
                  >
                    {nbImport.value > 30 && (
                      <span className="CurrentEnergy-segmentLabel">NB</span>
                    )}
                  </motion.div>
                  <motion.div
                    className="CurrentEnergy-segment CurrentEnergy-segmentWind"
                    style={{
                      width: `${(generation.wind / loadGauge.value) * 100}%`,
                    }}
                    title={`Wind: ${Math.round(generation.wind)} MW`}
                  >
                    {generation.wind > 30 && (
                      <span className="CurrentEnergy-segmentLabel">Wind</span>
                    )}
                  </motion.div>
                  <motion.div
                    className="CurrentEnergy-segment CurrentEnergy-segmentSolar"
                    style={{
                      width: `${(generation.solar / loadGauge.value) * 100}%`,
                    }}
                    title={`Solar: ${Math.round(generation.solar)} MW`}
                  >
                    {generation.solar > 20 && (
                      <span className="CurrentEnergy-segmentLabel">Solar</span>
                    )}
                  </motion.div>
                  <motion.div
                    className="CurrentEnergy-segment CurrentEnergy-segmentFossil"
                    style={{
                      width: `${(generation.fossil / loadGauge.value) * 100}%`,
                    }}
                    title={`Fossil: ${Math.round(generation.fossil)} MW`}
                  >
                    {generation.fossil > 20 && (
                      <span className="CurrentEnergy-segmentLabel">Fossil</span>
                    )}
                  </motion.div>
                </div>
                <div className="CurrentEnergy-segmentLegend">
                  <span className="CurrentEnergy-legendItem">
                    <span className="CurrentEnergy-legendDot CurrentEnergy-legendNB"></span>
                    NB: {Math.round(nbImport.value)} MW
                  </span>
                  <span className="CurrentEnergy-legendItem">
                    <span className="CurrentEnergy-legendDot CurrentEnergy-legendWind"></span>
                    Wind: {Math.round(generation.wind)} MW
                  </span>
                  <span className="CurrentEnergy-legendItem">
                    <span className="CurrentEnergy-legendDot CurrentEnergy-legendSolar"></span>
                    Solar: {Math.round(generation.solar)} MW
                  </span>
                  <span className="CurrentEnergy-legendItem">
                    <span className="CurrentEnergy-legendDot CurrentEnergy-legendFossil"></span>
                    Fossil: {Math.round(generation.fossil)} MW
                  </span>
                </div>
              </div>

              {/* 2. Available Capacity - Current renewables + max dispatchable */}
              {(() => {
                const availableCapacity =
                  generation.wind +
                  generation.solar +
                  CAPACITY.fossilFuelMax +
                  CAPACITY.nbImportMax;
                const availablePercent = Math.min(
                  100,
                  Math.round((loadGauge.value / availableCapacity) * 100),
                );
                return (
                  <div className="CurrentEnergy-capacitySection CurrentEnergy-capacitySectionAvailable">
                    <div className="CurrentEnergy-capacityHeader">
                      <span className="CurrentEnergy-capacityLabel">
                        Current Available Capacity
                      </span>
                      <span className="CurrentEnergy-capacityValue" style={{ color: getCapacityColor(availablePercent) }}>
                        <AnimatedNumber value={availablePercent} decimals={0} />
                        % of {Math.round(availableCapacity)} MW
                      </span>
                    </div>
                    <div className="CurrentEnergy-totalLoadBar CurrentEnergy-capacityGaugeBar">
                      <motion.div
                        className="CurrentEnergy-totalLoadFill"
                        style={{ background: getCapacityGradient(availablePercent) }}
                        initial={{
                          width: `${availablePercent}%`,
                        }}
                        animate={{
                          width: `${availablePercent}%`,
                        }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      />
                    </div>
                    <div className="CurrentEnergy-capacityDescription">
                      Current wind ({Math.round(generation.wind)} MW) + solar (
                      {Math.round(generation.solar)} MW) + max dispatchable
                      fuels ({CAPACITY.fossilFuelMax + CAPACITY.nbImportMax} MW)
                    </div>
                  </div>
                );
              })()}

              {/* 3. Capacity Without Wind + Solar - NB max + Fossil max */}
              {(() => {
                const reliablePercent = Math.min(100, Math.round((loadGauge.value / reliableCapacity) * 100));
                return (
                  <div className="CurrentEnergy-capacitySection CurrentEnergy-capacitySectionReliable">
                    <div className="CurrentEnergy-capacityHeader">
                      <span className="CurrentEnergy-capacityLabel">
                        Capacity Without Wind + Solar
                      </span>
                      <span className="CurrentEnergy-capacityValue" style={{ color: getCapacityColor(reliablePercent) }}>
                        <AnimatedNumber
                          value={reliablePercent}
                          decimals={0}
                        />
                        % of {reliableCapacity} MW
                      </span>
                    </div>
                    <div className="CurrentEnergy-totalLoadBar CurrentEnergy-capacityGaugeBar">
                      <motion.div
                        className="CurrentEnergy-totalLoadFill"
                        style={{ background: getCapacityGradient(reliablePercent) }}
                        initial={{
                          width: `${reliablePercent}%`,
                        }}
                        animate={{
                          width: `${reliablePercent}%`,
                        }}
                        transition={{ duration: 1, ease: "easeInOut" }}
                      />
                    </div>
                    <div className="CurrentEnergy-capacityDescription">
                      Firm NB imports + on-island generators = {reliableCapacity} MW
                    </div>
                  </div>
                );
              })()}

              {/* 4. Theoretical Max Capacity - All sources at max */}
              {(() => {
                const possiblePercent = Math.min(100, Math.round((loadGauge.value / possibleCapacity) * 100));
                return (
                  <div className="CurrentEnergy-capacitySection CurrentEnergy-capacitySectionPossible">
                    <div className="CurrentEnergy-capacityHeader">
                      <span className="CurrentEnergy-capacityLabel">
                        Theoretical Max Capacity
                      </span>
                      <span className="CurrentEnergy-capacityValue" style={{ color: getCapacityColor(possiblePercent) }}>
                        <AnimatedNumber
                          value={possiblePercent}
                          decimals={0}
                        />
                        % of {possibleCapacity} MW
                      </span>
                    </div>
                    <div className="CurrentEnergy-totalLoadBar CurrentEnergy-capacityGaugeBar">
                      <motion.div
                        className="CurrentEnergy-totalLoadFill"
                        style={{ background: getCapacityGradient(possiblePercent) }}
                        initial={{
                          width: `${possiblePercent}%`,
                        }}
                        animate={{
                          width: `${possiblePercent}%`,
                        }}
                        transition={{ duration: 1, ease: "easeInOut" }}
                      />
                    </div>
                    <div className="CurrentEnergy-capacityDescription">
                      All sources at max: NB + Fossil + Solar + Wind ={" "}
                      {possibleCapacity} MW
                    </div>
                  </div>
                );
              })()}

              <button
                className="CurrentEnergy-infoButton"
                onClick={() => setOpenSheet("totalLoad")}
                aria-label="More info about capacity"
              >
                i
              </button>
            </Card>
          )}
        </div>
      </div>

      {/* Generated Locally Section */}
      <div className="CurrentEnergy-section">
        <h3 className="CurrentEnergy-subsectionTitle">Generated Locally</h3>
        <div className="CurrentEnergy-grid">
          {energyData
            .filter(
              (g) =>
                g.gaugeType === "Total On-Island Wind Generation" ||
                g.gaugeType ===
                  "Total On-Island Solar plus Battery Generation" ||
                g.gaugeType === "Total On-Island Fossil Fuel Generation",
            )
            .map((gauge, i) => (
              <GaugeCard
                key={i}
                title={toTitle(gauge.gaugeType)}
                value={gauge.value}
                unit="MW"
                maxValue={gauge.maxValue}
                minValue={gauge.minValue}
                showBar={true}
                secondaryStat={
                  gauge.gaugeType === "Total On-Island Wind Generation" &&
                  weatherData?.peiAverage
                    ? {
                        value: (
                          <>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                marginRight: "4px",
                                verticalAlign: "middle",
                              }}
                            >
                              <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
                              <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
                              <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
                            </svg>
                            {weatherData.peiAverage.windSpeed} km/h
                          </>
                        ),
                        label: "",
                      }
                    : gauge.gaugeType ===
                          "Total On-Island Solar plus Battery Generation" &&
                        solarData?.islandAverage
                      ? {
                          value: (
                            <>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{
                                  marginRight: "4px",
                                  verticalAlign: "middle",
                                }}
                              >
                                <circle cx="12" cy="12" r="4" />
                                <path d="M12 2v2" />
                                <path d="M12 20v2" />
                                <path d="m4.93 4.93 1.41 1.41" />
                                <path d="m17.66 17.66 1.41 1.41" />
                                <path d="M2 12h2" />
                                <path d="M20 12h2" />
                                <path d="m6.34 17.66-1.41 1.41" />
                                <path d="m19.07 4.93-1.41 1.41" />
                              </svg>
                              {solarData.islandAverage.sunshineDuration != null
                                ? `${solarData.islandAverage.sunshineDuration.toFixed(1)}h`
                                : "—"}
                              {solarData.islandAverage.radiation != null && (
                                <> | {solarData.islandAverage.radiation} W/m²</>
                              )}
                            </>
                          ),
                          label: "",
                        }
                      : gauge.gaugeType ===
                            "Total On-Island Fossil Fuel Generation" &&
                          gauge.value > 0
                        ? (() => {
                            const emissions = estimateDieselEmissions(
                              gauge.value,
                            );
                            return {
                              value: (
                                <>
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{
                                      marginRight: "4px",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    <path d="M8 18V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v12" />
                                    <path d="M4 18h16" />
                                    <path d="M6 18v-2" />
                                    <path d="M18 18v-2" />
                                    <path d="M12 10v4" />
                                  </svg>
                                  {emissions.co2TonnesPerHour.toFixed(1)} t
                                  CO₂/h
                                </>
                              ),
                              label: "",
                            };
                          })()
                        : undefined
                }
              >
                {gauge.gaugeType === "Total On-Island Wind Generation" && (
                  <button
                    className="CurrentEnergy-infoButton"
                    onClick={() => setOpenSheet("wind")}
                    aria-label="More info about wind generation"
                  >
                    i
                  </button>
                )}
                {gauge.gaugeType ===
                  "Total On-Island Solar plus Battery Generation" && (
                  <button
                    className="CurrentEnergy-infoButton"
                    onClick={() => setOpenSheet("solar")}
                    aria-label="More info about solar generation"
                  >
                    i
                  </button>
                )}
                {gauge.gaugeType ===
                  "Total On-Island Fossil Fuel Generation" && (
                  <button
                    className="CurrentEnergy-infoButton"
                    onClick={() => setOpenSheet("fossil")}
                    aria-label="More info about fossil fuel generation"
                  >
                    i
                  </button>
                )}
              </GaugeCard>
            ))}
        </div>
      </div>

      {/* Renewables Distribution Section */}
      <div className="CurrentEnergy-section">
        <h3 className="CurrentEnergy-subsectionTitle">
          Renewables Distribution
        </h3>
        <div className="CurrentEnergy-grid">
          {energyData
            .filter(
              (g) =>
                g.gaugeType === "Renewable Power Used On Island" ||
                g.gaugeType === "Renewable Power Exported Off Island",
            )
            .map((gauge, i) => (
              <GaugeCard
                key={i}
                title={toTitle(gauge.gaugeType)}
                value={gauge.value}
                unit="MW"
                maxValue={gauge.maxValue}
                minValue={gauge.minValue}
                showBar={true}
              />
            ))}
        </div>
      </div>

      <div className="CurrentEnergy-timestamp">
        Last updated: {formatTimestamp(energyData[0].timestamp)}
      </div>

      {/* Bottom Sheets for contextual info */}
      <BottomSheet
        isOpen={openSheet === "totalLoad"}
        onClose={() => setOpenSheet(null)}
        title="Understanding Capacity Scenarios"
      >
        <div className="CurrentEnergy-sheetContent">
          <div className="CurrentEnergy-sheetSection">
            <strong>What is Capacity Without Wind + Solar?</strong>
            <p>
              Reliable capacity is the maximum amount of electricity PEI can
              depend on during peak conditions — combining firm imports from New
              Brunswick and the on-island generators that can be counted on to
              run.
            </p>
            <p>
              It excludes wind and solar because their output isn't guaranteed
              during winter peaks.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>
              Today's Capacity Without Wind + Solar: {reliableCapacity} MW
            </strong>
            <p>
              When demand approaches this level, the province is operating with
              little or no safety margin, meaning any outage on the mainland or
              on-island could trigger supply shortages.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>Theoretical Max Capacity ({possibleCapacity} MW):</strong>
            <p>
              Theoretical maximum if all sources (including wind and solar) are
              running at full capacity. This is not guaranteed and depends on
              weather conditions.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>References:</strong>
            <a
              href="https://irac.pe.ca/infocentre/documents/Electric-UE20944-Exhibit-M-9.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Maritime Electric Regulatory Filing (IRAC)
            </a>
            <a
              href="https://www.cbc.ca/news/canada/prince-edward-island/pei-maritime-electric-electricity-demand-1.7362625"
              target="_blank"
              rel="noopener noreferrer"
            >
              CBC News - Supply Not Keeping Up With Growth
            </a>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        isOpen={openSheet === "nbImport"}
        onClose={() => setOpenSheet(null)}
        title="Import from New Brunswick"
      >
        <div className="CurrentEnergy-sheetContent">
          <div className="CurrentEnergy-sheetSection">
            <strong>Capacity:</strong>
            <p>
              Physical capacity of 560 MW across four submarine cables.
              Practical limit of 300 MW due to NB transmission constraints.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <p>
              These imports supply most of PEI's electricity during typical
              demand periods.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>Sources:</strong>
            <a
              href="https://www.cer-rec.gc.ca/en/data-analysis/energy-markets/provincial-territorial-energy-profiles/provincial-territorial-energy-profiles-prince-edward-island.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Canada Energy Regulator - PEI Profile
            </a>
            <a
              href="https://irac.pe.ca/infocentre/documents/Electric-MECL-2020_Integrated_System-093020.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Maritime Electric 2020 Integrated System Plan
            </a>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        isOpen={openSheet === "wind"}
        onClose={() => setOpenSheet(null)}
        title="Wind Generation"
      >
        <div className="CurrentEnergy-sheetContent">
          <div className="CurrentEnergy-sheetSection">
            <strong>Installed Capacity:</strong>
            <p>{CAPACITY.windMax} MW (includes Eastern Kings wind farm)</p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <p>Wind is PEI's dominant domestic generation source.</p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>Current Wind Speed:</strong>
            <p>
              The wind speed shown is the PEI-wide average from weather stations
              across the island. Wind turbines typically generate power between
              12-90 km/h, with optimal output around 45-55 km/h.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>Sources:</strong>
            <a
              href="https://www.maritimeelectric.com/sustainability/environment/renewable-energy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Maritime Electric - Renewable Energy
            </a>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        isOpen={openSheet === "solar"}
        onClose={() => setOpenSheet(null)}
        title="Solar + Battery Generation"
      >
        <div className="CurrentEnergy-sheetContent">
          <div className="CurrentEnergy-sheetSection">
            <strong>Tracked Capacity:</strong>
            <p>{CAPACITY.solarMax} MW</p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>Breakdown:</strong>
            <p>
              31 MW utility-scale solar (PEI Energy Corporation 10 MW +
              Summerside 21 MW) plus 10 MW Summerside battery storage.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <p>
              Note: An additional 50-55 MW of distributed net-metered solar
              exists on PEI but is not tracked in this data.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>Current Conditions:</strong>
            <p>
              Sunshine duration shows today's accumulated hours of direct
              sunlight. Solar radiation (W/m²) measures current light intensity
              hitting panels — clear midday sun is typically 800-1000 W/m²,
              while overcast conditions drop to 100-300 W/m².
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>Sources:</strong>
            <a
              href="https://www.cer-rec.gc.ca/en/data-analysis/energy-markets/provincial-territorial-energy-profiles/provincial-territorial-energy-profiles-prince-edward-island.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Canada Energy Regulator - PEI Profile
            </a>
            <a
              href="https://www.maritimeelectric.com/services/services/net-metering/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Maritime Electric - Net Metering
            </a>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        isOpen={openSheet === "fossil"}
        onClose={() => setOpenSheet(null)}
        title="Fossil Fuel Generation"
      >
        <div className="CurrentEnergy-sheetContent">
          <div className="CurrentEnergy-sheetSection">
            <strong>Backup Capacity:</strong>
            <p>
              {CAPACITY.fossilFuelMax} MW (Maritime Electric combustion turbines
              + Summerside diesel units)
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <p>
              Runs infrequently, primarily during extreme peaks or when
              renewable output and import capacity are insufficient.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>Carbon Emissions:</strong>
            <p>
              When PEI's diesel generators run, they burn fuel and produce
              carbon emissions. The CO₂ estimate shown on the card represents
              the real-time environmental impact of on-island thermal
              generation.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>Emission Calculation:</strong>
            <p>
              Based on average diesel generator fuel consumption of 280 L/MWh
              and an emission factor of 2.68 kg CO₂ per liter of diesel burned.
            </p>
          </div>
          <div className="CurrentEnergy-sheetSection">
            <strong>Sources:</strong>
            <a
              href="https://irac.pe.ca/infocentre/documents/Electric-MECL-2020_Integrated_System-093020.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Maritime Electric 2020 Integrated System Plan
            </a>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}
